import apiClient from '../client'
import type { Conversation, ConversationFilters, PaginatedResponse } from '@/types'

// Stats returned by the API (total counts by type)
export interface ConversationStats {
  email: number
  call: number
  inbound_call: number
  advisor: number
  total: number
}

// Backend response type
interface BackendConversationsResponse {
  conversations: Conversation[]
  total: number
  limit: number
  offset: number
  stats?: ConversationStats  // Total counts by type (not affected by pagination)
}

// Extended paginated response with stats
export interface PaginatedConversationsResponse extends PaginatedResponse<Conversation> {
  stats?: ConversationStats
}

// Transform backend response to frontend PaginatedResponse format
const transformConversationsResponse = (
  backendData: BackendConversationsResponse,
  page: number,
  pageSize: number
): PaginatedConversationsResponse => ({
  items: backendData.conversations || [],
  total: backendData.total || 0,
  page: page,
  page_size: pageSize,
  total_pages: Math.ceil((backendData.total || 0) / pageSize),
  stats: backendData.stats,
})

export const conversationsApi = {
  getAll: async (filters: ConversationFilters = {}) => {
    const page = filters.page || 1
    const pageSize = filters.page_size || 25
    const offset = (page - 1) * pageSize

    // Map frontend 'type' to backend 'conversation_type'
    const { type, sort_by, sort_order, ...restFilters } = filters
    const params: Record<string, unknown> = {
      ...restFilters,
      limit: pageSize,
      offset: offset,
    }
    if (type) {
      params.conversation_type = type
    }
    // Pass sorting params to backend
    if (sort_by) {
      params.sort_by = sort_by
    }
    if (sort_order) {
      params.sort_order = sort_order
    }

    const response = await apiClient.get<BackendConversationsResponse>('/api/dashboard/conversations/all', {
      params,
    })

    return {
      ...response,
      data: transformConversationsResponse(response.data, page, pageSize),
    }
  },

  getById: (id: string) =>
    apiClient.get<Conversation>(`/api/dashboard/conversations/${id}`),

  // Get specific conversation by type and ID
  getInboundCall: (id: string | number) =>
    apiClient.get<{ inbound_call: Conversation }>(`/api/dashboard/conversations/inbound/${id}`),

  getCallSummary: (id: string | number) =>
    apiClient.get<{ call_summary: Conversation }>(`/api/dashboard/conversations/call-summary/${id}`),

  getByLead: async (leadId: string, filters: ConversationFilters = {}) => {
    const page = filters.page || 1
    const pageSize = filters.page_size || 25
    const offset = (page - 1) * pageSize

    const response = await apiClient.get<BackendConversationsResponse>(`/api/dashboard/conversations/lead/${leadId}`, {
      params: {
        ...filters,
        limit: pageSize,
        offset: offset,
      },
    })

    return {
      ...response,
      data: transformConversationsResponse(response.data, page, pageSize),
    }
  },

  delete: (id: string) =>
    apiClient.delete(`/api/dashboard/conversations/${id}`),

  getTags: () =>
    apiClient.get<{ tags: string[] }>('/api/dashboard/conversations/tags'),

  // Tag management for outbound calls (CallSummary)
  addTag: (callId: string | number, tag: string) =>
    apiClient.post<{ tags: string[]; message: string }>(
      `/api/dashboard/conversations/call/${callId}/tags/add`,
      null,
      { params: { tag } }
    ),

  removeTag: (callId: string | number, tag: string) =>
    apiClient.delete<{ tags: string[]; message: string }>(
      `/api/dashboard/conversations/call/${callId}/tags/${encodeURIComponent(tag)}`
    ),

  // Tag management for inbound calls
  addInboundTag: (callId: string | number, tag: string) =>
    apiClient.post<{ tags: string[]; message: string }>(
      `/api/dashboard/conversations/inbound/${callId}/tags/add`,
      null,
      { params: { tag } }
    ),

  removeInboundTag: (callId: string | number, tag: string) =>
    apiClient.delete<{ tags: string[]; message: string }>(
      `/api/dashboard/conversations/inbound/${callId}/tags/${encodeURIComponent(tag)}`
    ),

  // Share conversation via email
  share: (data: {
    recipient_email: string
    conversation_type: string
    conversation_id: string
    message?: string
  }) =>
    apiClient.post<{
      success: boolean
      message: string
      share_url: string
    }>('/api/dashboard/conversations/share', data),
}
